import React from "react";
import PropTypes from "prop-types";
import { withFormik, Field } from "formik";
import { useTranslation } from "react-i18next";
import {
  dataModel,
  validationDataModel,
  setInitialValues,
} from "./meetingFormNew.data.form";
import { InputSelect } from "@de-prado-dev/react-component-library";
import {
  FormContainer,
  Header,
  HeaderTitle,
  HeaderSubtitle,
  FormBody,
  FormSection,
  SectionLabel,
  LocationsContainer,
  LocationPanel,
  PanelHeader,
  ItemCount,
  SearchBox,
  LocationList,
  LocationItem,
  TransferButtons,
  TransferButton,
  ButtonsContainer,
  CancelButton,
  ActionButton,
  HelpText,
} from "./meetingFormNew.styled";

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="search-input"
    />
  );
}

const MeetingFormNew = (props) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    edit,
    courses,
    onSelect,
    onDelete,
    onSelectAll,
    onDeleteAll,
    filterCenter,
    filter,
    centersBack,
    centers,
    centersSelected,
    selectedAvailable,
    selectedToMove,
    onToggleAvailable,
    onToggleToMove,
    onMoveSelected,
    onMoveBackSelected,
    onBack,
    onCourseChange,
    values,
  } = props;

  // Call onCourseChange when courseUuid changes
  React.useEffect(() => {
    if (values?.courseUuid && onCourseChange) {
      onCourseChange(values.courseUuid);
    }
  }, [values?.courseUuid, onCourseChange]);

  const renderAvailableLocations = () => {
    const locationsToRender =
      centersBack && centersBack.length > 0 && filter.length > 0
        ? centersBack
        : centers;

    if (!locationsToRender || locationsToRender.length === 0) {
      return (
        <div className="no-items">
          {t("forms.meeting.labels.noLocationsAvailable")}
        </div>
      );
    }

    return locationsToRender.map((center) => {
      const isSelected = selectedAvailable.some(
        (c) => c.value === center.value
      );
      return (
        <LocationItem
          key={`available_${center.value}`}
          $selected={isSelected}
          onClick={(e) => onToggleAvailable(center, e.ctrlKey || e.metaKey)}
          onDoubleClick={() => onSelect(center)}
        >
          <span>{center.label}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(center);
            }}
            className="action-btn"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </LocationItem>
      );
    });
  };

  const renderSelectedLocations = () => {
    if (!centersSelected || centersSelected.length === 0) {
      return (
        <div className="no-items-selected">
          <p>{t("forms.meeting.labels.noLocationsSelected")}</p>
        </div>
      );
    }

    return centersSelected.map((center) => {
      const isSelected = selectedToMove.some((c) => c.value === center.value);
      return (
        <LocationItem
          key={`selected_${center.value}`}
          $selected={isSelected}
          onClick={(e) => onToggleToMove(center, e.ctrlKey || e.metaKey)}
          onDoubleClick={() => onDelete(center)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(center);
            }}
            className="action-btn"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          <span>{center.label}</span>
        </LocationItem>
      );
    });
  };
  const months = [
    { month: t("forms.meeting.constants.january"), value: "01" },
    { month: t("forms.meeting.constants.february"), value: "02" },
    { month: t("forms.meeting.constants.march"), value: "03" },
    { month: t("forms.meeting.constants.april"), value: "04" },
    { month: t("forms.meeting.constants.may"), value: "05" },
    { month: t("forms.meeting.constants.june"), value: "06" },
    { month: t("forms.meeting.constants.july"), value: "07" },
    { month: t("forms.meeting.constants.august"), value: "08" },
    { month: t("forms.meeting.constants.september"), value: "09" },
    { month: t("forms.meeting.constants.october"), value: "10" },
    { month: t("forms.meeting.constants.november"), value: "11" },
    { month: t("forms.meeting.constants.december"), value: "12" },
  ];
  const actualYear = new Date().getFullYear();

  const years = Array.from({ length: 6 }, (_, i) => {
    const year = actualYear + i;
    return { value: year, label: year.toString() };
  });

  return (
    <>
      <FormContainer>
        <Header>
          <HeaderTitle>
            {edit ? t("forms.meeting.titleEdit") : t("forms.meeting.titleNew")}
          </HeaderTitle>
          <HeaderSubtitle>
            {/* {edit
              ? t("forms.meeting.subtitleEdit")
              : t("forms.meeting.subtitle")} */}
            {/* <BreadCrumb items={breads} /> */}
          </HeaderSubtitle>
        </Header>

        <FormBody>
          <form autoComplete="off" onSubmit={handleSubmit}>
            {/* Course Selection */}
            <FormSection>
              <SectionLabel mandatory>
                {t("forms.meeting.labels.course")}
              </SectionLabel>
              <Field
                id="courseUuid"
                name="courseUuid"
                component={InputSelect}
                placeholder={t("forms.meeting.placeholders.selectCourse")}
                optionLabel="name"
                optionValue="uuid"
                options={courses}
                classNamePrefix="dark"
                addOptionEmpty
              />
            </FormSection>

            {/* Month Selection */}
            <div className="row">
              <div className="col-6">
                <FormSection>
                  <SectionLabel mandatory>
                    {t("forms.meeting.labels.month")}
                  </SectionLabel>
                  <Field
                    id="month"
                    name="month"
                    component={InputSelect}
                    placeholder={t("forms.meeting.placeholders.selectMonth")}
                    optionLabel="month"
                    optionValue="value"
                    options={months}
                    classNamePrefix="dark"
                    addOptionEmpty
                  />
                </FormSection>
              </div>
              <div className="col-6">
                <FormSection>
                  <SectionLabel mandatory>
                    {t("forms.meeting.labels.year")}
                  </SectionLabel>
                  <Field
                    id="year"
                    name="year"
                    component={InputSelect}
                    options={years}
                    optionLabel="label"
                    optionValue="value"
                    classNamePrefix="dark"
                    placeholder={t("forms.meeting.placeholders.selectYear")}
                    addOptionEmpty
                  />
                </FormSection>
              </div>
            </div>

            {!edit && (
              <>
                {/* Locations Section */}
                <FormSection>
                  <SectionLabel mandatory>
                    {t("forms.meeting.labels.locations")}
                  </SectionLabel>
                  <HelpText>{t("forms.meeting.help.selectLocations")}</HelpText>

                  <LocationsContainer>
                    {/* Available Locations */}
                    <LocationPanel>
                      <PanelHeader>
                        <span>
                          {t("forms.meeting.labels.availableLocations")}
                        </span>
                        <ItemCount>
                          {centers ? centers.length : 0}{" "}
                          {t("forms.meeting.labels.items")}
                        </ItemCount>
                      </PanelHeader>
                      <SearchBox>
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <DebouncedInput
                          onChange={(value) => filterCenter(value)}
                          placeholder={t(
                            "forms.meeting.labels.searchPlaceholder"
                          )}
                          type="text"
                          value={filter ?? ""}
                        />
                      </SearchBox>
                      <LocationList>{renderAvailableLocations()}</LocationList>
                    </LocationPanel>

                    {/* Transfer Buttons */}
                    <TransferButtons>
                      <TransferButton
                        type="button"
                        onClick={() => onSelectAll()}
                        disabled={!centers || centers.length === 0}
                        title={t("forms.meeting.tooltips.moveAllToSelected")}
                      >
                        <i className="fa-solid fa-angles-right"></i>
                      </TransferButton>
                      <TransferButton
                        type="button"
                        onClick={() => onMoveSelected()}
                        disabled={
                          !selectedAvailable || selectedAvailable.length === 0
                        }
                        title={t("forms.meeting.tooltips.moveToSelected")}
                      >
                        <i className="fa-solid fa-chevron-right"></i>
                      </TransferButton>
                      <TransferButton
                        type="button"
                        onClick={() => onMoveBackSelected()}
                        disabled={
                          !selectedToMove || selectedToMove.length === 0
                        }
                        title={t("forms.meeting.tooltips.moveToAvailable")}
                      >
                        <i className="fa-solid fa-chevron-left"></i>
                      </TransferButton>
                      <TransferButton
                        type="button"
                        onClick={() => onDeleteAll()}
                        disabled={
                          !centersSelected || centersSelected.length === 0
                        }
                        title={t("forms.meeting.tooltips.moveAllToAvailable")}
                      >
                        <i className="fa-solid fa-angles-left"></i>
                      </TransferButton>
                    </TransferButtons>

                    {/* Selected Locations */}
                    <LocationPanel>
                      <PanelHeader>
                        <span>
                          {t("forms.meeting.labels.selectedLocations")}
                        </span>
                        <ItemCount selected>
                          {centersSelected ? centersSelected.length : 0}{" "}
                          {t("forms.meeting.labels.items")}
                        </ItemCount>
                      </PanelHeader>
                      <SearchBox>
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <input
                          type="text"
                          placeholder={t(
                            "forms.meeting.labels.searchPlaceholder"
                          )}
                          className="search-input"
                        />
                      </SearchBox>
                      <LocationList>{renderSelectedLocations()}</LocationList>
                    </LocationPanel>
                  </LocationsContainer>
                  {/* <HelpText>
                    <i className="fa-solid fa-lightbulb"></i>{" "}
                    {t("forms.meeting.help.tip")}
                  </HelpText> */}
                </FormSection>
              </>
            )}

            {/* Action Buttons */}
            <ButtonsContainer>
              <CancelButton type="button" onClick={() => onBack()}>
                {t("forms.form.buttons.cancel")}
              </CancelButton>
              <ActionButton type="submit">
                {edit
                  ? t("forms.form.buttons.save")
                  : t("forms.meeting.titleNew")}
              </ActionButton>
            </ButtonsContainer>
          </form>
        </FormBody>
      </FormContainer>
    </>
  );
};

MeetingFormNew.propTypes = {
  handleSubmit: PropTypes.func,
  initialValues: PropTypes.object,
  onback: PropTypes.func,
  edit: PropTypes.bool,
};

const MeetingFormNewF = withFormik({
  mapPropsToValues: (props) => {
    if (props.initialValues) {
      return setInitialValues(props.initialValues);
    }
    return dataModel;
  },
  validationSchema: (props) => {
    return validationDataModel(props.edit);
  },
  handleSubmit: (values, { props, resetForm }) => {
    props.onSubmit(values, resetForm);
  },
  enableReinitialize: true,
  displayName: "MeetingFormNew",
})(MeetingFormNew);

export default MeetingFormNewF;
